import './styles/ReviewControls.scss';
import React, { useState } from 'react';
import { detectOverflow } from '@popperjs/core';
import ReviewCommentEditor from './ReviewCommentEditor';
import Routes from '../../../../routes';
import classNames from 'classnames';
import history from 'shared_history';
import { Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router';
import { ReviewContextType, ReviewType } from '../../../propTypes';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft';
import { faBars } from '@fortawesome/pro-solid-svg-icons/faBars';
import { faChevronCircleDown } from '@fortawesome/pro-solid-svg-icons/faChevronCircleDown';
import { faChevronCircleUp } from '@fortawesome/pro-solid-svg-icons/faChevronCircleUp';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';


/**
 * Review navigation controls.
 *
 * Shows the current page name, has next/previous buttons, and a dropdown menu of pages to select. Also provides
 * editor for the current page's comment.
 *
 * @param review the review object
 * @param reviewContext the review context
 * @return {JSX.Element}
 */
const ReviewControls = ({ review, reviewContext }) => {
  const { paginator } = reviewContext;
  const { currPage, prevPage, nextPage, pages } = paginator;
  const [open, setOpen] = useState(false);
  const commentable = currPage.commentRef
    && review && review.editable;
  const isSummaryComment = currPage.commentRef && currPage.commentRef.field == 'scores_comment'

  const overflowToColumns = {
    name: 'overflowToColumns',
    enabled: true,
    phase: 'write',
    fn({ state, instance }) {
      const overflowValue = detectOverflow(state);
      if (overflowValue.top > 0) {
        if (state.styles.popper.columns > 0) { // size of window changed
          state.styles.popper.columns = state.styles.popper.columns + 1;
        } else { // first time applying columns
          const currentHeight = state.elements.popper.offsetHeight;
          const availableHeight = currentHeight - overflowValue.top - 50;
          state.styles.popper.columns = Math.ceil(currentHeight / availableHeight);
        }
        instance.update();
      }
    }
  };

  return (<React.Fragment>
      <div className={classNames('review-controls-padding', { open: open })}/>
      <div className="review-controls">
        {commentable && <div className="row">
          <div className="col-24">
            <div className={`review-controls-comments ${isSummaryComment ? 'summary' : ''}`}>
              <div className="review-controls-comments-left"/>
              <div className={`review-controls-comments-tab ${isSummaryComment ? 'summary-tab' : ''}`} onClick={() => setOpen(!open)}>

                <div className="review-controls-comments-tab-label">
                <span>
                  {isSummaryComment ? 'Summary Comments' : 'Comments'}
                </span>
                {' '}
                  <FontAwesomeIcon icon={open ? faChevronCircleUp : faChevronCircleDown}/>
                </div>
              </div>
              <div className="review-controls-comments-right"/>
            </div>
          </div>
        </div>}

        <div className="review-controls-inner">
          {open && commentable && <div className="review-controls-comments-body">
            {isSummaryComment ?
              <p>Enter your overall comments about this candidate. We’ll save it to your Review Summary for this applicant</p> :
              <p>Add your comments about this document. We’ll save it to your Review Summary for this applicant.</p>
            }

            <ReviewCommentEditor review={review} commentRef={currPage.commentRef}/>
          </div>}

          <div className="row">
            <div className="col-6">
              <div className="review-controls-prev">
                {prevPage && <Link className="btn btn-primary" to={prevPage.href}>
                  <FontAwesomeIcon icon={faChevronLeft}/> {currPage.prevText || 'Previous'}
                </Link>}
              </div>
            </div>

            <div className="col-12">
              <div className="review-controls-page">
                Currently Reading:{' '}
                <span className="review-controls-page-name">{currPage.name}</span>
                <span className="review-controls-page-divider">|</span>

                <Dropdown drop="up" onSelect={(href) => {
                  if (href === 'back') {
                    window.location = Routes.admissions_reader_reviews_path({
                      school_year_id: review.school_year_id, clear: false
                    });
                  } else {
                    history.push(href);
                  }
                }}>
                  <Dropdown.Toggle className="review-controls-page-button">
                    <FontAwesomeIcon icon={faBars}/>
                  </Dropdown.Toggle>
                  <Dropdown.Menu popperConfig={{ modifiers: [overflowToColumns] }}>
                    {pages.map((page, i) => (
                      <Dropdown.Item key={i} eventKey={page.href} className="review-pages-menu-item">
                        <FontAwesomeIcon icon={page.icon}/>{' '}{page.name}
                      </Dropdown.Item>))}
                    <Dropdown.Divider className='review-pages-menu-full-width' />
                    <Dropdown.Item
                      eventKey="back"
                      className='review-pages-menu-item review-pages-menu-full-width'
                    >
                      <FontAwesomeIcon icon={faArrowLeft}/> Back to My Reviews
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>

            <div className="col-6">
              <div className="review-controls-next">
                {nextPage && <Link
                  className={classNames('btn', currPage.nextBtnClass || 'btn-primary')}
                  to={nextPage.href}>
                  {currPage.nextText || nextPage.name} <FontAwesomeIcon icon={faChevronRight}/>
                </Link>}
              </div>
            </div>

          </div>
        </div>
      </div>
    </React.Fragment>);
};

ReviewControls.propTypes = {
  review: ReviewType.isRequired,
  reviewContext: ReviewContextType.isRequired
};

export default ReviewControls;
